import React, { useContext, useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import {
    Box,
    IconButton,
    Tab,
    Tabs,
    tabsClasses,
} from '@mui/material';
import {
    Close,
    CloudSync,
} from '@mui/icons-material';

import { useFetch } from '../components/auth/useFetch';
import { useSnackbar } from 'notistack';

import PermissionGuard from '../contexts/PermissionContext';
import ProjectContext from '../contexts/ProjectContext';
import UserContext from '../contexts/UserContext';

import ControlBar from '../components/structure/ControlBar';
import ControlButton from '../components/structure/ControlButton';
import ControlDivider from '../components/structure/ControlDivider';

import { itemIcons } from '../components/SideItems/SideItems';

import AssetDetailDataGrid from '../components/datagrid/AssetDetailDataGrid';
import AssetLabrollDataGrid from '../components/datagrid/AssetLabrollDataGrid';
import AssetOverviewDataGrid from '../components/datagrid/AssetOverviewDataGrid';

import ConfirmMASVTransferDialog from '../components/dialog/ConfirmMASVTransferDialog';


export default function AssetTrackingView(props) {

    const { makeTour, setIsLoading } = props
    const { isAuthenticated } = useAuth0();
    const fetchWithAuth = useFetch();
    const { enqueueSnackbar } = useSnackbar();
    const projectContext = useContext(ProjectContext);
    const { konsolUser } = useContext(UserContext)

    const [isTransferConfirmationOpen, setIsTransferConfirmationOpen] = useState(false)
    const [masvConfig, setMasvConfig] = useState({});
    const [rows, setRows] = useState([])
    const [rowsLoading, setRowsLoading] = useState(false)
    const [tabs, setTabs] = useState([{'name': 'Asset Tracker', 'type': 'main'}])
    const [transferDate, setTransferDate] = useState(null)
    const [value, setValue] = useState(0);


    useEffect(() => {
        isAuthenticated && (() => {
            retrieveMasvConfig();
        })()
    }, [isAuthenticated])

    const handleChange = (event, newValue) => {
        setRows([])
        setValue(newValue);
        setTransferDate(tabs[newValue]?.transfer_date ?? null)
    };


    const handleClose = (e, tabIndex) => {
        e.stopPropagation()
        const newTabs = tabs.filter((_, index) => index !== tabIndex);
        let newValue = value;
        if (tabIndex === value){
            newValue = value > 0 ? value - 1 : 0
            setRows([])
            setTransferDate(tabs[newValue]?.transfer_date ?? null)
        }
        else if (tabIndex < value) {
            newValue = value - 1
        }
        setTabs(newTabs)
        setValue(newValue)
    }


    const handleLabrollDetail = (row) => {
        if (!tabs.some(tab => tab.name === row.labroll)) {
            setTabs([...tabs, { 'name': row.labroll, 'type': 'lab_roll', transfer_date: row.transfer_date }]);
        }
    }

    const handleTransferDateDetail = (row) => {
        if (!tabs.some(tab => tab.name === row.transfer_date)) {
            setTabs([...tabs, { 'name': row.transfer_date, 'type': 'transfer_date', transfer_date: row.transfer_date }]);
        }
    }


    const handleTransfer = async () => {
        setIsLoading(true);
        setIsTransferConfirmationOpen(false);
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/masv/`, {
                method: 'POST',
                headers: {
                    'X-Project-ID': projectContext?.project_db,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    package_name: transferDate,
                })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data);
            }
            // Subscribe to PubNub channel for transfer updates
            // subscribeToPubNub(data?.progress_channel);

            retrieveAssetOverview();
        } catch (error) {
            enqueueSnackbar(
                error?.message?.message ?? 'Failed to initiate MASV transfer',
                { variant: 'error' }
            );
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditorialCheck = async (transferDate, newResult) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/editorialhistory/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Project-ID": projectContext?.project_db,
                },
                body: JSON.stringify({
                    transfer_date: transferDate,
                    result: newResult,
                    editor: `${konsolUser?.given_name ?? ''} ${konsolUser?.family_name ?? ''}`
                }),
            });
            if (!response.ok) {
                throw new Error("Failed to update editorial signoff");
            }
            retrieveAssetOverview();
        } catch (e) {
            enqueueSnackbar('Failed to update Editorial signoff', { variant: 'error' });
            console.error("Failed to update Editorial signoff", e);
        }
    };

    const retrieveAssetOverview = async () => {
        setIsLoading(true)
        setRowsLoading(true)
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/assetoverview`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then(response => response.json())
            .then(result => {
                setRows(result)
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => {
                setIsLoading(false)
                setRowsLoading(false)
            })
    }

    const retrieveAssetDetail = async (params) => {
        setIsLoading(true)
        setRowsLoading(true)
        const queryString = new URLSearchParams(params).toString();
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/assetdetail/${queryString ? `?${queryString}` : ''}`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then(response => response.json())
            .then(result => {
                setRows(result)
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => {
                setIsLoading(false)
                setRowsLoading(false)
            })
    }

    const retrieveAssetLabRoll = async (params) => {
        setIsLoading(true)
        setRowsLoading(true)
        const queryString = new URLSearchParams(params).toString();
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/assetlabroll/${queryString ? `?${queryString}` : ''}`, {
            headers: {
                'Content-Type': 'application/json',
                'X-Project-ID': projectContext?.project_db
            },
        })
            .then(response => response.json())
            .then(result => {
                setRows(result)
            })
            .catch(e => {
                console.error(e);
            })
            .finally(() => {
                setIsLoading(false)
                setRowsLoading(false)
            })
    }

    const retrieveMasvConfig = async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/masv/configuration`, {
                headers: {
                    'X-Project-ID': projectContext?.project_db,
                },
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error();
            }
            setMasvConfig(data);
        } catch (error) {
            console.error('Error fetching MASV configuration');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        makeTour([
            {
                title: 'Asset Tracker',
                target: '#asset-tracker-view-table',
                content: 'View a summary of received assets across all transfer dates here. Click on the open icon for an in-depth view of assets for that transfer date.',
                placementBeacon: 'top'
            },
            {
                title: 'Asset Tracker',
                target: '#asset-tracker-view-tabs',
                content: 'Switch between each selected transfer date by clicking on the different tabs.',
            },
        ])
    }, [])

    return (
        <React.Fragment>
            <Box display="flex" flexDirection="column" height='100%'>
                <div id='asset-tracker-view-tabs'>
                <ControlBar>
                <Box
                        sx={{
                            maxWidth: '80vw',
                        }}
                    >
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            indicatorColor="secondary"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                [`& .${tabsClasses.scrollButtons}`]: {
                                '&.Mui-disabled': { opacity: 0.2 },
                                },
                            }}
                        >
                            {tabs.map((tab, index) => (
                                <Tab 
                                    key={index}
                                    style={{ 
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        color: value === index ? 'rgb(255, 255, 255)' : 'rgb(150, 150, 150)'
                                    }}

                                    label={
                                        tab['type'] === 'main' ? 
                                            <Box display="flex" alignItems="center" >
                                                {itemIcons[tab['name']]}
                                                <Box ml={1}>{tab['name']}</Box>
                                            </Box>
                                            :
                                            <Box
                                                display={'flex'}
                                                height={'20px'}
                                                alignItems={'center'}
                                            >
                                                <Box display="flex" alignItems="center" >
                                                    {itemIcons[tab['name']]}
                                                    <Box ml={1}>{tab['name']}</Box>
                                                </Box>
                                                {index > 0 && (<IconButton

                                                    onClick={(e) => handleClose(e, index)}
                                                    sx={{
                                                        color: 'grey',
                                                        '&:hover': {
                                                            color: 'white'
                                                        }
                                                    }}
                                                >
                                                    <Close sx={{ fontSize: {xs: '20px', md: '14px'} }}/>
                                                </IconButton>)}
                                            </Box>
                                    }
                                />
                            ))}
                        </Tabs>
                    </Box>
                    <PermissionGuard permission={'transfer:media'}>
                    {masvConfig?.enabled &&
                        <Box>
                            <ControlDivider />
                            <ControlButton
                                title={`Transfer ${transferDate}`}
                                clicked={() => setIsTransferConfirmationOpen(true)}
                                disabled={transferDate === null}
                            >
                                <CloudSync />
                            </ControlButton>
                        </Box>
                    }
                    </PermissionGuard>
                </ControlBar>
                </div>
                <div id='asset-tracker-view-table' style={{ height: '100%', width: '100%' }}>
                    { tabs[value]['type'] === 'main' && (
                        <AssetOverviewDataGrid 
                            handleTransferDateDetail={handleTransferDateDetail}
                            handleEditorialCheck={handleEditorialCheck}
                            retrieveAssetOverview={retrieveAssetOverview}
                            rows={rows}
                            rowsLoading={rowsLoading}
                            setTransferDate={(transferDate) => setTransferDate(transferDate)}
                            transferEnabled={masvConfig?.enabled ?? false}
                        />
                    )}
                    { tabs[value]?.type === 'transfer_date' && (
                        <AssetDetailDataGrid
                            handleLabrollDetail={handleLabrollDetail}
                            retrieveAssetDetail={retrieveAssetDetail}
                            rows={rows}
                            rowsLoading={rowsLoading}
                            transferdate={tabs[value]['name']}
                        />
                    )}
                    { tabs[value]['type'] === 'lab_roll' && (
                        <AssetLabrollDataGrid
                            labroll={tabs[value]['name']}
                            retrieveAssetLabRoll={retrieveAssetLabRoll}
                            rows={rows}
                            rowsLoading={rowsLoading}
                        />
                    )}
                </div>
                <ConfirmMASVTransferDialog
                    clickAway={() => setIsTransferConfirmationOpen(false)}
                    destinationBucket={masvConfig?.downstream_connection?.destination ?? ''}
                    handleClick={handleTransfer}
                    masvConfig={masvConfig}
                    open={isTransferConfirmationOpen}
                    packageName={transferDate}
                    transferToTargetRoot={masvConfig?.downstream_connection?.transfer_to_target_root ?? false}
                    targetDirectoryID={masvConfig?.downstream_connection?.target_directory_id ?? ''}
                />
            </Box>
        </React.Fragment>
    )
}